import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { subjectName, workspaceTag } from './format'
import { MONO } from './recipes'
import type { SubjectSummary } from './types'

export interface SubjectLabelProps {
  subject: Pick<
    SubjectSummary,
    'captureTag' | 'subjectNo' | 'givenName' | 'surname' | 'sex' | 'enrolledAt'
  >
  /** Extra readouts after the name, already formatted; `mono` for dates, times and durations. */
  details?: readonly { text: string; mono?: boolean }[]
  /** `tag-first` (default) for headers: "ZA-0142 · Neznámy"; `name-first` for rows and cards. */
  order?: 'tag-first' | 'name-first'
  className?: string
}

/**
 * The one way a subject is named: "ZA-0142 · Neznámy · 29. 9. 2026 · 18:42". The tag and the
 * readouts are mono and never break (no "ZA-" / "0142" split); the name is sans and truncates
 * first. Size and colour come from the parent. Without a capture tag the tag is the enrolment
 * time and the short id ("Neznámy 14:05 · 7f3a") when the enrolment time is known.
 */
export function SubjectLabel({
  subject,
  details = [],
  order = 'tag-first',
  className,
}: SubjectLabelProps) {
  const { t, i18n } = useTranslation()
  const nameFirst = order === 'name-first'
  // Name-first rows and cards keep the tag as a quiet secondary readout, so a narrow column
  // truncates neither the name nor the tag.
  const tag = (
    <span
      className={cn(
        MONO,
        'shrink-0 whitespace-nowrap',
        nameFirst
          ? 'text-[0.87em] font-medium text-muted-foreground'
          : 'font-semibold text-foreground'
      )}
    >
      {workspaceTag(subject, i18n.language)}
    </span>
  )
  const name = <span className="truncate">{subjectName(subject, t)}</span>
  return (
    <span className={cn('inline-flex min-w-0 items-baseline gap-1.5', className)}>
      {nameFirst ? name : tag}
      {!nameFirst && <span aria-hidden>·</span>}
      {nameFirst ? tag : name}
      {details.map(detail => (
        <span key={detail.text} className="contents">
          <span aria-hidden>·</span>
          <span className={cn('whitespace-nowrap', detail.mono && MONO)}>{detail.text}</span>
        </span>
      ))}
    </span>
  )
}
